import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { PaymentMethod, PaymentType } from '@klubovna/api-client';
import { TranslateModule } from '@ngx-translate/core';
import { QrCodeService } from '@posServices';
import { ButtonColor, ButtonComponent, PAYMENT_IBAN } from '@shared';
import { from, of, switchMap } from 'rxjs';

import { PaymentSubmitEvent, TabPaymentFormInputs } from './tab-payment-form.model';

@Component({
  selector: 'app-tab-payment-form',
  standalone: true,
  imports: [ ButtonComponent, DecimalPipe, FormsModule, TranslateModule ],
  templateUrl: './tab-payment-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TabPaymentFormComponent {
  private destroyRef = inject(DestroyRef);
  private qrCodeService = inject(QrCodeService);

  readonly ButtonColor = ButtonColor;
  readonly PaymentMethod = PaymentMethod;
  readonly PaymentType = PaymentType;
  readonly PAYMENT_IBAN = PAYMENT_IBAN;

  data = input.required<TabPaymentFormInputs>();
  submitting = input<boolean>(false);
  cancel = output<void>();
  submit = output<PaymentSubmitEvent>();

  paymentAmount = signal<number>(0);
  selectedPaymentMethod = signal<PaymentMethod>(PaymentMethod.Cash);
  selectedPaymentType = signal<PaymentType>(PaymentType.Money);
  selectedQtyByProduct = signal<Map<string, number>>(new Map());

  qrCodeDataUrl = signal<string | null>(null);
  currentVariableSymbol = signal<string | null>(null);

  private qrTrigger = computed(() => ({
    isCardPayment: this.selectedPaymentMethod() === PaymentMethod.Card,
    isMoneyPayment: this.selectedPaymentType() === PaymentType.Money,
    amount: this.paymentAmount(),
  }));

  constructor() {
    toObservable(this.qrTrigger).pipe(
      switchMap(({ isCardPayment, isMoneyPayment, amount }) => {
        if (isCardPayment && isMoneyPayment && amount > 0) {
          const amountCents = Math.round(amount * 100);
          const variableSymbol = this.currentVariableSymbol() || this.qrCodeService.generateVariableSymbol();
          if (!this.currentVariableSymbol()) {
            this.currentVariableSymbol.set(variableSymbol);
          }
          const payBySquareData = this.qrCodeService.generatePayBySquareData(amountCents, variableSymbol);
          return from(this.qrCodeService.generateQrCodeDataUrl(payBySquareData));
        }
        this.currentVariableSymbol.set(null);
        return of(null);
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(dataUrl => {
      this.qrCodeDataUrl.set(dataUrl);
    });
  }

  unpaidItemsWithPoints = computed(() => this.data().unpaidItemsWithPoints);

  userPointBalance = computed(() => this.data().userPointBalance ?? 0);

  selectedItemsPoints = computed(() => {
    const selection = this.selectedQtyByProduct();
    return this.unpaidItemsWithPoints().reduce((total, item) => {
      const selectedQty = selection.get(item.productId) || 0;
      return total + selectedQty * item.unitPoints;
    }, 0);
  });

  hasEnoughPointsForSelected = computed(() => {
    return this.userPointBalance() >= this.selectedItemsPoints();
  });

  hasAnySelected = computed(() => {
    const selection = this.selectedQtyByProduct();
    return Array.from(selection.values()).some(qty => qty > 0);
  });

  canSubmit = computed(() => {
    if (this.selectedPaymentType() === PaymentType.Money) {
      return this.paymentAmount() > 0;
    } else {
      return this.hasAnySelected() && this.hasEnoughPointsForSelected();
    }
  });

  resetForm(): void {
    const initialAmount = this.data().orderBalanceCents / 100;
    this.paymentAmount.set(initialAmount);
    this.selectedQtyByProduct.set(new Map());
    this.selectedPaymentType.set(PaymentType.Money);
    this.selectedPaymentMethod.set(PaymentMethod.Cash);
    this.currentVariableSymbol.set(null);
  }

  selectPaymentMethod(method: PaymentMethod): void {
    this.selectedPaymentMethod.set(method);
  }

  selectPaymentType(type: PaymentType): void {
    this.selectedPaymentType.set(type);
    if (type === PaymentType.Money) {
      this.selectedQtyByProduct.set(new Map());
    }
  }

  getSelectedQty(productId: string): number {
    return this.selectedQtyByProduct().get(productId) || 0;
  }

  setSelectedQty(productId: string, qty: number): void {
    const item = this.unpaidItemsWithPoints().find(i => i.productId === productId);
    if (!item) return;

    const newMap = new Map(this.selectedQtyByProduct());
    const clampedQty = Math.max(0, Math.min(qty, item.pointsEligibleQty));
    if (clampedQty === 0) {
      newMap.delete(productId);
    } else {
      newMap.set(productId, clampedQty);
    }
    this.selectedQtyByProduct.set(newMap);
  }

  incrementSelectedQty(productId: string): void {
    const current = this.getSelectedQty(productId);
    this.setSelectedQty(productId, current + 1);
  }

  decrementSelectedQty(productId: string): void {
    const current = this.getSelectedQty(productId);
    this.setSelectedQty(productId, current - 1);
  }

  selectAllItems(): void {
    const newMap = new Map<string, number>();
    this.unpaidItemsWithPoints().forEach(item => {
      newMap.set(item.productId, item.pointsEligibleQty);
    });
    this.selectedQtyByProduct.set(newMap);
  }

  deselectAllItems(): void {
    this.selectedQtyByProduct.set(new Map());
  }

  onCancel(): void {
    this.cancel.emit();
  }

  onSubmit(): void {
    if (!this.canSubmit()) return;

    if (this.selectedPaymentType() === PaymentType.Money) {
      const amountCents = Math.round(this.paymentAmount() * 100);
      this.submit.emit({
        type: PaymentType.Money,
        amountCents,
        method: this.selectedPaymentMethod(),
      });
    } else {
      this.submit.emit({
        type: PaymentType.Points,
        selectedByProduct: new Map(this.selectedQtyByProduct()),
      });
    }
  }
}
